// AuthContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { getDeviceId } from "../utils/deviceId";

const API = process.env.REACT_APP_API_URL;
const AuthContext = createContext(null);

// helpers storage (session, no persistente)
const ACCESS_TOKEN_KEY = "scenathon_access_token";
const REFRESH_TOKEN_KEY = "scenathon_refresh_token";
const CSRF_STORAGE_KEY = "scenathon_csrf_token";
const SELECTED_SCENATHON_KEY = "scenathon_selected_scenathon";
const PENDING_TASK_KEY = "scenathon_pending_task_v1";

function readSelectedScenathonFromStorage() {
  try {
    const raw = sessionStorage.getItem(SELECTED_SCENATHON_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.id) return null;
    return parsed;
  } catch {
    return null;
  }
}

function readPendingTaskFromStorage() {
  try {
    const raw = localStorage.getItem(PENDING_TASK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const id = Number(parsed.id);
    if (!Number.isFinite(id) || id <= 0) return null;
    return {
      ...parsed,
      id,
    };
  } catch {
    return null;
  }
}

/** =========================
 * JWT helpers (client-side)
 * ========================= */
function getJwtExpMs(token) {
  try {
    const [, payloadB64] = token.split(".");
    if (!payloadB64) return 0;
    const payloadJson = atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(payloadJson);
    return payload?.exp ? payload.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

function shouldRefreshSoon(token, thresholdMs = 2 * 60 * 1000) {
  const expMs = getJwtExpMs(token);
  if (!expMs) return true;
  return expMs - Date.now() <= thresholdMs;
}

export function AuthProvider({ children }) {
  // ✅ Recupera access token del sessionStorage si existe
  const [accessToken, setAccessToken] = useState(() => {
    try {
      return sessionStorage.getItem(ACCESS_TOKEN_KEY);
    } catch {
      return null;
    }
  });
  const [user, setUser] = useState(null);
  const [selectedScenathon, setSelectedScenathon] = useState(
    () => readSelectedScenathonFromStorage(),
  );
  const [bootstrapping, setBootstrapping] = useState(true);

  // CSRF: guardado en sessionStorage
  const [csrfToken, setCsrfToken] = useState(() => {
    try {
      return sessionStorage.getItem(CSRF_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const [pendingTask, setPendingTask] = useState(() =>
    readPendingTaskFromStorage(),
  );
  const [pendingTaskState, setPendingTaskState] = useState("idle");

  // evita refresh simultáneos
  const refreshPromiseRef = useRef(null);

  // ✅ Guarda access token en sessionStorage
  const saveAccessToken = useCallback((token) => {
    const newToken = token || null;
    setAccessToken(newToken);
    try {
      if (newToken) sessionStorage.setItem(ACCESS_TOKEN_KEY, newToken);
      else sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    } catch {
      // ignore storage errors
    }
  }, []);

  // ✅ Guarda refresh token en sessionStorage (fallback si cookies bloqueadas)
  const saveRefreshToken = useCallback((token) => {
    try {
      if (token) sessionStorage.setItem(REFRESH_TOKEN_KEY, token);
      else sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch {
      // ignore storage errors
    }
  }, []);

  const saveCsrf = useCallback((t) => {
    const token = t || null;
    setCsrfToken(token);
    try {
      if (token) sessionStorage.setItem(CSRF_STORAGE_KEY, token);
      else sessionStorage.removeItem(CSRF_STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  const saveSelectedScenathon = useCallback((scenathon) => {
    setSelectedScenathon(scenathon || null);
    try {
      if (scenathon) sessionStorage.setItem(SELECTED_SCENATHON_KEY, JSON.stringify(scenathon));
      else sessionStorage.removeItem(SELECTED_SCENATHON_KEY);
    } catch {
      // ignore storage errors
    }
  }, []);

  const savePendingTask = useCallback((task) => {
    setPendingTask(task || null);
    try {
      if (task) localStorage.setItem(PENDING_TASK_KEY, JSON.stringify(task));
      else localStorage.removeItem(PENDING_TASK_KEY);
    } catch {
      // ignore storage errors
    }
  }, []);

  const clearPendingTask = useCallback(() => {
    savePendingTask(null);
    setPendingTaskState("idle");
  }, [savePendingTask]);

  const startPendingTask = useCallback(
    ({ id, name, country, colname, overwrite }) => {
      const taskId = Number(id);
      if (!Number.isFinite(taskId) || taskId <= 0) return;
      savePendingTask({
        id: taskId,
        name: name || "",
        country: country || "",
        colname: colname || "",
        overwrite: !!overwrite,
        startedAt: new Date().toISOString(),
      });
      setPendingTaskState("todo");
    },
    [savePendingTask],
  );

  /**
   * ✅ REFRESH:
   * - Usa cookie refresh (preferido)
   * - Si no hay cookie, usa token de sessionStorage (fallback para cookies bloqueadas)
   * - devuelve access token nuevo o null
   */
  const refresh = useCallback(async () => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    refreshPromiseRef.current = (async () => {
      // Intenta refresh con cookie + sessionStorage como fallback
      const body = {};
      let refreshTokenFromStorage = null;
      
      // Si no hay cookie, intenta recuperar del sessionStorage
      try {
        refreshTokenFromStorage = sessionStorage.getItem(REFRESH_TOKEN_KEY);
      } catch {}
      
      if (refreshTokenFromStorage) {
        body.refreshToken = refreshTokenFromStorage;
      }

      const res = await fetch(`${API}/api/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "y-device-id": getDeviceId(),
        },
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
      });

      if (!res.ok) {
        saveAccessToken(null);
        setUser(null);
        saveCsrf(null);
        saveRefreshToken(null);
        return null;
      }

      const data = await res.json().catch(() => ({}));
      const token = data.token || null;

      saveAccessToken(token); // ✅ Guarda en sessionStorage
      setUser(data.user || null);
    if (data.selectedScenathon) saveSelectedScenathon(data.selectedScenathon);

      // ✅ si backend devuelve refreshToken, guardarlo en sessionStorage
      if (data.refreshToken) saveRefreshToken(data.refreshToken);

      // ✅ si backend devuelve csrfToken, guardarlo
      if (data.csrfToken) saveCsrf(data.csrfToken);

      return token;
    })();

    try {
      return await refreshPromiseRef.current;
    } finally {
      refreshPromiseRef.current = null;
    }
  }, [saveAccessToken, saveRefreshToken, saveCsrf]);

  // ✅ Bootstrapping: al montar intenta refresh con cookie HttpOnly
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        await refresh();
      } catch {
        // silencioso
      } finally {
        if (alive) setBootstrapping(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [refresh]);

  // ✅ Sliding expiration: si hay actividad y el token está por vencer, refresca
  useEffect(() => {
    if (bootstrapping) return;

    let lastActivity = Date.now();
    let mounted = true;

    const markActivity = () => {
      lastActivity = Date.now();
    };

    const maybeRefresh = async () => {
      if (!mounted) return;
      if (document.visibilityState !== "visible") return;

      // si no hay access token, intenta revivir sesión con refresh cookie
      if (!accessToken) {
        await refresh();
        return;
      }

      const idleMs = Date.now() - lastActivity;

      const ACTIVE_WINDOW = 5 * 60_000; // 5 min
      const THRESHOLD = 2 * 60_000; // refrescar si expira en <= 2 min

      if (
        idleMs <= ACTIVE_WINDOW &&
        shouldRefreshSoon(accessToken, THRESHOLD)
      ) {
        await refresh();
      }
    };

    const events = ["mousemove", "keydown", "scroll", "click", "touchstart"];
    events.forEach((ev) =>
      window.addEventListener(ev, markActivity, { passive: true }),
    );

    const onFocus = () => {
      void maybeRefresh();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") void maybeRefresh();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    const interval = setInterval(() => {
      void maybeRefresh();
    }, 30_000);

    return () => {
      mounted = false;
      clearInterval(interval);

      events.forEach((ev) => window.removeEventListener(ev, markActivity));
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [bootstrapping, accessToken, refresh]);

  /**
   * ✅ login: setea token, user, y guarda csrf en sessionStorage
   */
  const login = useCallback(
    ({ token, refreshToken, user, csrfToken, selectedScenathon }) => {
      saveAccessToken(token || null); // ✅ Guarda access token en sessionStorage
      saveRefreshToken(refreshToken || null); // ✅ Guarda refresh token como fallback
      setUser(user || null);
      saveCsrf(csrfToken || null); // ✅ guarda el CSRF real
      saveSelectedScenathon(selectedScenathon || null);
    },
    [saveAccessToken, saveRefreshToken, saveCsrf, saveSelectedScenathon],
  );

  /**
   * ✅ logout: NO CSRF
   */
  const logout = useCallback(async ({ skipRequest = false } = {}) => {
    try {
      if (!skipRequest) {
        const body = {};

        try {
          const refreshTokenFromStorage = sessionStorage.getItem(REFRESH_TOKEN_KEY);
          if (refreshTokenFromStorage) {
            body.refreshToken = refreshTokenFromStorage;
          }
        } catch {
          // ignore storage errors
        }

        await fetch(`${API}/api/auth/logout`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "y-device-id": getDeviceId(),
          },
          body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
        });
      }
    } finally {
      saveAccessToken(null); // ✅ Limpia sessionStorage
      saveRefreshToken(null); // ✅ Limpia refresh token fallback
      setUser(null);
      saveSelectedScenathon(null);
      saveCsrf(null);
      clearPendingTask();
    }
  }, [saveAccessToken, saveRefreshToken, saveCsrf, clearPendingTask]);

  /**
   * ✅ ensureAuth:
   */
  const ensureAuth = useCallback(async () => {
    if (accessToken) return accessToken;
    return await refresh();
  }, [accessToken, refresh]);

  /**
   * ✅ authFetch:
   */
  const authFetch = useCallback(
    async (path, options = {}) => {
      const url = path.startsWith("http") ? path : `${API}${path}`;
      const method = (options.method || "GET").toUpperCase();

      const token = await ensureAuth();

      const headers = {
        ...(options.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const needsCsrf = ["POST", "PUT", "PATCH", "DELETE"].includes(method);
      if (needsCsrf && csrfToken) headers["y-csrf-token"] = csrfToken;

      const firstResp = await fetch(url, {
        ...options,
        credentials: "include",
        headers,
      });

      if (firstResp.status !== 401) return firstResp;

      const newToken = await refresh();
      if (!newToken) return firstResp;

      // importante: usar el csrfToken actual (puede haber cambiado con refresh)
      const csrfNow = (() => {
        try {
          return sessionStorage.getItem(CSRF_STORAGE_KEY);
        } catch {
          return csrfToken;
        }
      })();

      const retryHeaders = {
        ...(options.headers || {}),
        Authorization: `Bearer ${newToken}`,
      };
      if (needsCsrf && csrfNow) retryHeaders["y-csrf-token"] = csrfNow;

      return fetch(url, {
        ...options,
        credentials: "include",
        headers: retryHeaders,
      });
    },
    [ensureAuth, refresh, csrfToken],
  );

  // Track pending task globally (DISABLED: tasktodo endpoints no longer exist)
  // Polling mechanism removed - tasktodo table and endpoints are not available in this version

  const value = useMemo(
    () => ({
      accessToken,
      user,
      selectedScenathon,
      csrfToken,
      bootstrapping,
      login,
      logout,
      ensureAuth,
      authFetch,
      pendingTask,
      pendingTaskState,
      startPendingTask,
      clearPendingTask,
      setAccessToken: saveAccessToken,
      setUser,
      setSelectedScenathon: saveSelectedScenathon,
      setCsrfToken: saveCsrf,
    }),
    [
      accessToken,
      user,
      csrfToken,
      bootstrapping,
      login,
      logout,
      ensureAuth,
      authFetch,
      pendingTask,
      pendingTaskState,
      startPendingTask,
      clearPendingTask,
      saveAccessToken,
      saveRefreshToken,
      saveCsrf,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider />");
  return ctx;
}
